import {
  Search,
  MessagesSquare,
  CalendarCheck,
  FileText,
  Languages,
  Image as ImageIcon,
  FileSearch,
  ScanSearch,
  Sparkles,
  ArrowUp,
  Server,
  ShieldCheck,
  Lock,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { BunkerMark } from './BunkerMark';

/**
 * Where a tile leads: the tour screen `id` to open. `scenarioId` disambiguates
 * the two Dok.-Analyse previews that share one screen.
 */
export type TileTarget = { id: string; scenarioId?: string };

interface StartScreenProps {
  className?: string;
  /** Called with the tile target (scenario or screen) when a tile is clicked. */
  onTileClick?: (target: TileTarget) => void;
}

interface WorkflowTile {
  icon: LucideIcon;
  /** i18n key (under the `start` prefix) for the tile title. */
  titleKey: string;
  /** i18n key (under the `start` prefix) for the tile subtitle. */
  subKey: string;
  /** Preview-only use case — keeps the amber "Kommt bald" label but stays clickable. */
  comingSoon?: boolean;
  /** Scenario or screen this tile opens. */
  target: TileTarget;
}

const TILES: WorkflowTile[] = [
  { icon: Search, titleKey: 'tileKnowledgeTitle', subKey: 'tileKnowledgeSub', target: { id: 'chat' } },
  { icon: MessagesSquare, titleKey: 'tileCustomerTitle', subKey: 'tileCustomerSub', target: { id: 'briefpost' } },
  { icon: CalendarCheck, titleKey: 'tileMeetingTitle', subKey: 'tileMeetingSub', target: { id: 'gespraeche' } },
  { icon: FileText, titleKey: 'tileProposalTitle', subKey: 'tileProposalSub', target: { id: 'angebote' } },
  { icon: Languages, titleKey: 'tileTranslateTitle', subKey: 'tileTranslateSub', target: { id: 'translate' } },
  { icon: ImageIcon, titleKey: 'tileImageTitle', subKey: 'tileImageSub', target: { id: 'image' } },
  { icon: FileSearch, titleKey: 'tileContractCheckTitle', subKey: 'tileContractCheckSub', comingSoon: true, target: { id: 'doc-intelligence', scenarioId: 'contractCheck' } },
  { icon: ScanSearch, titleKey: 'tileDocAnalysisTitle', subKey: 'tileDocAnalysisSub', comingSoon: true, target: { id: 'doc-intelligence', scenarioId: 'docAnalysis' } },
];

export function StartScreen({ className, onTileClick }: StartScreenProps) {
  const { t } = useTranslation('demo');
  return (
    <div className={`bnk-start${className ? ` ${className}` : ''}`}>
      <style>{startScreenCss}</style>

      <div className="bnk-start-wordmark-wrap">
        <div className="bnk-start-bloom" aria-hidden="true" />
        <BunkerMark size={52} className="bnk-start-mark" title="bunker.ai" />
        <h1 className="bnk-start-wordmark">
          bunker<span className="bnk-start-dot">.ai</span>
        </h1>
        <p className="bnk-start-tagline">{t('start.tagline')}</p>
      </div>

      <h2 className="bnk-start-greeting">{t('start.greeting')}</h2>

      <div className="bnk-start-grid" role="list">
        {TILES.map((tile) => {
          const Icon = tile.icon;
          return (
            <button
              key={tile.titleKey}
              type="button"
              role="listitem"
              className={`bnk-start-tile${tile.comingSoon ? ' is-soon' : ''}`}
              onClick={() => onTileClick?.(tile.target)}
            >
              {tile.comingSoon && <span className="bnk-start-badge">{t('start.comingSoon')}</span>}
              <span className="bnk-start-tile-icon" aria-hidden="true">
                <Icon size={22} strokeWidth={1.75} color="#2D7EFF" />
              </span>
              <span className="bnk-start-tile-title">{t(`start.${tile.titleKey}`)}</span>
              <span className="bnk-start-tile-sub">{t(`start.${tile.subKey}`)}</span>
            </button>
          );
        })}
      </div>

      <div className="bnk-start-command">
        <Sparkles size={18} strokeWidth={1.75} color="#00D4FF" aria-hidden="true" />
        <input
          type="text"
          className="bnk-start-input"
          placeholder={t('start.inputPlaceholder')}
          aria-label={t('start.inputAriaLabel')}
        />
        <button type="button" className="bnk-start-send" aria-label={t('start.sendAriaLabel')}>
          <ArrowUp size={18} strokeWidth={2.25} color="#FFFFFF" aria-hidden="true" />
        </button>
      </div>

      <div className="bnk-start-trust">
        <span className="bnk-start-trust-item">
          <Server size={14} strokeWidth={1.75} aria-hidden="true" /> {t('start.trustOnPremise')}
        </span>
        <span className="bnk-start-trust-sep" aria-hidden="true">·</span>
        <span className="bnk-start-trust-item">
          <ShieldCheck size={14} strokeWidth={1.75} aria-hidden="true" /> {t('start.trustDsgvo')}
        </span>
        <span className="bnk-start-trust-sep" aria-hidden="true">·</span>
        <span className="bnk-start-trust-item">
          <Lock size={14} strokeWidth={1.75} aria-hidden="true" /> {t('start.trustCloudAct')}
        </span>
      </div>
    </div>
  );
}

const startScreenCss = `
.bnk-start {
  font-family: Inter, system-ui, sans-serif;
  color: #E2E8F0;
  max-width: 880px;
  margin: 0 auto;
  padding: 48px 24px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.bnk-start-wordmark-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 28px;
}
.bnk-start-mark {
  position: relative;
  margin-bottom: 14px;
  filter: drop-shadow(0 8px 22px rgba(45, 126, 255, 0.40));
}
.bnk-start-bloom {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 420px;
  height: 220px;
  transform: translate(-50%, -50%);
  background: radial-gradient(ellipse at center, rgba(45,126,255,0.30) 0%, rgba(45,126,255,0.08) 40%, transparent 70%);
  filter: blur(8px);
  pointer-events: none;
  animation: bnk-start-bloom-pulse 6s ease-in-out infinite;
}
@keyframes bnk-start-bloom-pulse {
  0%, 100% { opacity: 0.75; }
  50% { opacity: 1; }
}
.bnk-start-wordmark {
  position: relative;
  margin: 0;
  font-size: 52px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #E2E8F0;
  line-height: 1;
}
.bnk-start-dot { color: #2D7EFF; }
.bnk-start-tagline {
  position: relative;
  margin: 14px 0 0;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.28em;
  color: #94A3C2;
}
.bnk-start-greeting {
  margin: 0 0 24px;
  font-size: 22px;
  font-weight: 600;
  color: #E2E8F0;
}
.bnk-start-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  width: 100%;
  margin-bottom: 28px;
}
.bnk-start-tile {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: 18px 16px;
  text-align: left;
  border-radius: 14px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  cursor: pointer;
  transition: transform 0.18s ease, border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
}
.bnk-start-tile:hover {
  transform: translateY(-3px);
  border-color: #2D7EFF;
  background: rgba(45,126,255,0.06);
  box-shadow: 0 10px 30px -12px rgba(45,126,255,0.45);
}
.bnk-start-tile:focus-visible {
  outline: 2px solid #2D7EFF;
  outline-offset: 2px;
}
/* Coming-soon tiles stay clickable (they open a tailored preview) but read as
 * dimmed so the amber "Kommt bald" label is honest about their status. */
.bnk-start-tile.is-soon {
  opacity: 0.62;
}
.bnk-start-tile.is-soon:hover {
  opacity: 1;
}
.bnk-start-tile-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(45,126,255,0.12);
}
.bnk-start-tile-title {
  font-size: 14px;
  font-weight: 700;
  color: #E2E8F0;
  line-height: 1.25;
}
.bnk-start-tile-sub {
  font-size: 12px;
  font-weight: 400;
  color: #94A3C2;
  line-height: 1.35;
}
.bnk-start-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #FBBF24;
  background: rgba(251,191,36,0.12);
  border: 1px solid rgba(251,191,36,0.30);
}
.bnk-start-command {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 12px 12px 18px;
  border-radius: 16px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  margin-bottom: 18px;
}
.bnk-start-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-family: inherit;
  font-size: 15px;
  color: #E2E8F0;
}
.bnk-start-input::placeholder { color: #4A5568; }
.bnk-start-send {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 11px;
  background: #2D7EFF;
  cursor: pointer;
  transition: background 0.18s ease, transform 0.18s ease;
}
.bnk-start-send:hover { background: #1F6BEF; transform: translateY(-1px); }
.bnk-start-trust {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: #94A3C2;
}
.bnk-start-trust-item { display: inline-flex; align-items: center; gap: 6px; }
.bnk-start-trust-sep { color: #30415F; }
@media (max-width: 720px) {
  .bnk-start-grid { grid-template-columns: repeat(2, 1fr); }
  .bnk-start-wordmark { font-size: 42px; }
}
/* phones: keep tiles in TWO compact columns (single column made the screen an
   endless scroll) and tighten the wordmark block; cap the decorative bloom so
   it never pushes horizontal overflow past the narrow demo content area. */
@media (max-width: 640px) {
  .bnk-start { padding: 24px 14px 20px; }
  .bnk-start-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 18px; }
  .bnk-start-wordmark-wrap { margin-bottom: 16px; }
  .bnk-start-mark { margin-bottom: 10px; }
  .bnk-start-wordmark { font-size: 34px; }
  .bnk-start-tagline { letter-spacing: 0.2em; }
  .bnk-start-bloom { width: 100%; max-width: 300px; height: 170px; }
  .bnk-start-greeting { font-size: 19px; margin-bottom: 16px; }
  .bnk-start-tile { padding: 12px; gap: 8px; }
  .bnk-start-tile-icon { width: 40px; height: 40px; }
  .bnk-start-tile-title { font-size: 13px; }
  .bnk-start-trust { flex-wrap: wrap; justify-content: center; row-gap: 4px; }
}

/* ── light-mode overrides (accents/glow stay) ── */
:root:not(.dark) .bnk-start,
:root:not(.dark) .bnk-start-wordmark,
:root:not(.dark) .bnk-start-greeting,
:root:not(.dark) .bnk-start-tile-title,
:root:not(.dark) .bnk-start-input { color: #0f172a; }
:root:not(.dark) .bnk-start-tagline,
:root:not(.dark) .bnk-start-tile-sub,
:root:not(.dark) .bnk-start-trust { color: #475569; }
:root:not(.dark) .bnk-start-input::placeholder { color: #94a3b8; }
:root:not(.dark) .bnk-start-trust-sep { color: #cbd5e1; }
:root:not(.dark) .bnk-start-tile,
:root:not(.dark) .bnk-start-command { background: #f8fafc; border-color: #e2e8f0; }
:root:not(.dark) .bnk-start-tile:hover { background: rgba(45,126,255,0.06); border-color: #2563eb; }
`;
